import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Collapse } from "react-bootstrap";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import { toFormData } from "../../../minions/forms";
import ModuleComsaSideBarReports from "./ModuleComsaSideBarReports";
import { renderCommaFloat } from "../../../minions/int";
import Observations from "../../../layout/screenSpecifics/Modules/ComsaReports/Observations";
import Translator from "../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import { axiosPath } from "../../../routing/helpers/router";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import _ from "lodash";
import { useDispatch } from 'react-redux';
import { getFile, handleMinDate } from '@/layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools';
import errorHandler from '@/layout/components/error/ErrorHandler';

const ModuleComsaWaterWasteReportScreen = () => {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [reportList, setReportList] = useState([]);
  const [showSendReportModal, setShowSendReportModal] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState(null);
  const [reportOptions, setReportOptions] = useState({});
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    }))
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState();
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [reportType] = useState("waterWaste");
  const dynamicAreaButton = useRef(null);
  const [filters, setFilters] = useState({
    month: null,
    year: null,
    totalWaterWasteFrom: null,
    totalWaterWasteTo: null,
    wasteOrigin: null,
    wasteDestination: null,
  });
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterYear, setFilterYear] = useState(null);
  const [filterTotalWaterWasteFrom, setFilterTotalWaterWasteFrom] =
    useState(null);
  const [filterTotalWaterWasteTo, setFilterTotalWaterWasteTo] = useState(null);
  const [filterWasteOrigin, setFilterWasteOrigin] = useState(null);
  const [filterWasteDestination, setFilterWasteDestination] = useState(null);
  const [actualFilters, setActualFilters] = useState({
    month: null,
    year: null,
    totalWaterWasteFrom: null,
    totalWaterWasteTo: null,
    wasteOrigin: null,
    wasteDestination: null,
  });
  const [moduleData, setModuleData] = useState(null);

  // > FILTERS
  const handleFilters = () => {
    const newFilters = {
      month: filterMonth,
      year: filterYear,
      totalWaterWasteFrom: filterTotalWaterWasteFrom,
      totalWaterWasteTo: filterTotalWaterWasteTo,
      wasteOrigin: filterWasteOrigin,
      wasteDestination: filterWasteDestination,
    };
    if (!_.isEqual(newFilters, actualFilters)) {
      setActualFilters(newFilters);
      setLoading(true);
      setFilters(newFilters);
    }
  };
  const handleRemoveFilters = () => {
    const newFilters = {
      month: null,
      year: null,
      totalWaterWasteFrom: null,
      totalWaterWasteTo: null,
      wasteOrigin: null,
      wasteDestination: null,
    };
    setFilterMonth(null);
    setFilterYear(null);
    setFilterTotalWaterWasteFrom(null);
    setFilterTotalWaterWasteTo(null);
    setFilterWasteOrigin(null);
    setFilterWasteDestination(null);
    setActualFilters(newFilters);
    setLoading(true);
    setFilters(newFilters);
  };
  // < FILTERS

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_getModuleData'), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then(({ data }) => {
        if (isMounted.current) setModuleData(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);
  // > LOAD MODULE DATA //

  // > LOAD REPORT OPTIONS //
  const loadOptions = useCallback(async () => {
    await axios.post(
        axiosPath('api_r_user_module_comsaReports_reportOptions'),
        { type: reportType, moduleBridgeHex, subModuleBridgeHex },
      )
      .then(({ data }) => {
        if (isMounted.current) setReportOptions(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [reportType, moduleBridgeHex, subModuleBridgeHex]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);
  // > LOAD REPORT OPTIONS //

  // > LOAD REPORTS //
  const loadReports = useCallback(async () => {
    await axios.post(
        axiosPath("api_r_user_module_comsaReports_report"),
        {
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          filters,
        }
      )
      .then(({ data }) => {
        if (isMounted.current) {
          setReportList(data);
          setIsFiltersPanelOpen(false);
          setLoading(false);
        }
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [filters, reportType, moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    if (loading) {
      loadReports();
    }
  }, [loading, loadReports]);
  // < LOAD REPORTS //

  // < ADD REPORT
  const addReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_addReport"),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (err) {}
  };

  const handleAddReport = () => {
    setShowSendReportModal(true);
    setReportFormInitialValues({
      id: null,
      month: null,
      year: null,
      dynamicArea: [
        {
          totalWaterWaste: "",
          evidence: null,
          wasteOrigin: null,
          wasteDestination: null,
        },
      ],
      evidence: 0,
      comment: ''
    });
  };
  // < ADD REPORT

  // < EDIT REPORT
  const editReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_editReport"),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (err) {}
  };

  const handleEditReport = (report) => {
    const editableReport = _.omit(report, [
      "totalWaterWaste",
      "wasteOrigin",
      "wasteDestination",
    ]);
    setReportFormInitialValues({
      ...editableReport,
      month: Number(report.month),
      year: report.year,
      dynamicArea: [
        {
          totalWaterWaste: report.totalWaterWaste,
          wasteOrigin: Number(report.wasteOrigin),
          wasteDestination: Number(report.wasteDestination),
        },
      ],
      evidence: 0,
      comment: report.comment
    });
    setShowSendReportModal(true);
  };
  // < EDIT REPORT

  // < DELETE REPORT
  const removeReport = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_removeReport"),
        {
          id: deleteReportId,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }
      );
      return response.data;
    } catch (err) {}
  });

  useEffect(() => {
    if (deleteReportId) setShowDeleteModal(true);
  }, [deleteReportId]);

  const handleOnDeleteModalConfirm = () => {
    removeReport().then((res) => {
      setReportList(reportList.filter((r) => r.id !== res.id));
      setDeleteReportId(null);
      setShowDeleteModal(false);
    });
  };

  const handleOnDeleteModalClose = () => {
    setDeleteReportId(null);
    setShowDeleteModal(false);
  };
  // < DELETE REPORT

  const removeDynamicArea = (dynamicArea, arrayHelpers, i) => {
    if (dynamicArea.length > 1) arrayHelpers.remove(i);
  };

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    month: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    year: Yup.date()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    dynamicArea: Yup.array().of(
      Yup.object().shape({
        totalWaterWaste: Yup.string()
          .typeError(
            ReactDOMServer.renderToString(<Translator file="module-comsaReports" name="wrong.format" />)
          )
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />
            )
          )
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        wasteOrigin: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        wasteDestination: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
      })
    ),
  });
  // < FORM SCHEMA

  // < SAVE FORM
  const handleSaveReport = (values) => {
    setIsSendingReport(true);
    const reportFormFunction = values.id
      ? editReport(values)
      : addReport(values);
    reportFormFunction
      .then(() => {
        if (isMounted.current) {
          setShowSendReportModal(false);
          setLoading(true);
        }
      })
      .finally(() => {
        if (isMounted.current) setIsSendingReport(false);
      });
  };
  // < SAVE FORM

  const getWaterWasteEvidence = (typeId, salt) => axios
      .post(
          axiosPath('api_r_user_module_comsaReports_getWaterWasteEvidence'),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            typeId,
            salt,
          }
      )
      .then(({ data }) => (window.location = data))
      .catch((e) => dispatch(errorHandler(e)))

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  ),
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="water.waste" />,
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_water_waste"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator file="module-comsaReports" name="water.waste" />
            </Title>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <span
              className="btn btn-success btn-sm mr-2"
              onClick={() => handleAddReport()}
              title={ReactDOMServer.renderToString(
                <Translator file="module-comsaReports" name="new.fuel.use" />,
              )}
            >
              <AppRemixIcon icon="add" />
            </span>
            <span
              className={`btn btn-sm btn-${
                isFiltersPanelOpen ? '' : 'outline-'
              }info float-right`}
              onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
            >
              <Translator file="module-comsaReports" name="filters" />
            </span>
            {(filters.month ||
              filters.year ||
              filters.totalWaterWasteFrom ||
              filters.totalWaterWasteTo ||
              filters.wasteOrigin ||
              filters.wasteDestination) && (
              <span
                className="btn btn-danger btn-sm mr-1 float-right"
                onClick={() => handleRemoveFilters()}
              >
                <AppRemixIcon icon="delete-bin-6" />
              </span>
            )}
          </div>
        </div>
        <Observations reportType={reportType} />
        <div className="row mt-3">
          <div className="col-12">
            <Collapse in={isFiltersPanelOpen}>
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={filterYear}
                        onChange={(e) => setFilterYear(e)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={months}
                        onChange={(e) => setFilterMonth(e)}
                        title={<Translator file="module-saicaReports" name="month" />}
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={filterMonth}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="total.water.waste"
                          />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="from" />,
                        )})`}
                        icon={<AppRemixIcon icon="contrast-drop" />}
                        value={filterTotalWaterWasteFrom || ''}
                        onChange={(number) => setFilterTotalWaterWasteFrom(number)}
                        decimals={3}
                        append={
                          <div className="input-group-append">
                            <span className="input-group-text">m3</span>
                          </div>
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="total.water.waste"
                          />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="to" />,
                        )})`}
                        icon={<AppRemixIcon icon="contrast-drop" />}
                        value={filterTotalWaterWasteTo || ''}
                        onChange={(number) => setFilterTotalWaterWasteTo(number)}
                        decimals={3}
                        append={
                          <div className="input-group-append">
                            <span className="input-group-text">m3</span>
                          </div>
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <AppSingleSelect
                          options={
                            !_.isEmpty(reportOptions) && reportOptions.wasteOrigin
                          }
                          onChange={(e) => setFilterWasteOrigin(e)}
                          title={
                            <Translator
                              file="module-comsaReports"
                              name="waste.origin"
                            />
                          }
                          icon={<AppRemixIcon icon="contrast-drop-2" />}
                          placeholder={ReactDOMServer.renderToString(
                            <Translator
                              file="module-comsaReports"
                              name="all.waste.origins"
                            />,
                          )}
                          value={filterWasteOrigin}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <AppSingleSelect
                          options={
                            !_.isEmpty(reportOptions) &&
                            reportOptions.wasteDestination
                          }
                          onChange={(e) => setFilterWasteDestination(e)}
                          title={
                            <Translator
                              file="module-comsaReports"
                              name="waste.destination"
                            />
                          }
                          icon={<AppRemixIcon icon="contrast-drop-2" />}
                          placeholder={ReactDOMServer.renderToString(
                            <Translator
                              file="module-comsaReports"
                              name="all.waste.destinations"
                            />,
                          )}
                          value={filterWasteDestination}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="float-right">
                        <span
                          className="btn btn-info"
                          onClick={() => handleFilters()}
                        >
                          <Translator file="module-saicaReports" name="filter" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <div className="card sr-header">
              <div className="card-body">
                {loading || _.isEmpty(reportOptions) ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <>
                    {!_.isEmpty(reportList) ? (
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th className="col-1">
                              <Translator file="module-comsaReports" name="year" />
                            </th>
                            <th className="col-1">
                              <Translator file="module-comsaReports" name="month" />
                            </th>
                            <th className="col-1">
                              <Translator
                                file="module-comsaReports"
                                name="total.water.waste"
                              />{' '}
                              (m3)
                            </th>
                            <th className="col-1">
                              <Translator
                                file="module-comsaReports"
                                name="waste.origin"
                              />
                            </th>
                            <th className="col-1">
                              <Translator
                                file="module-comsaReports"
                                name="waste.destination"
                              />
                            </th>
                            <th className="col-1">
                              <Translator
                                file="module-comsaReports"
                                name="evidence"
                              />
                            </th>
                            <th className="col-5">
                              <Translator
                                file="module-comsaReports"
                                name="m-cr-comment"
                              />
                            </th>
                            <th className="col-1" />
                          </tr>
                        </thead>
                        <tbody>
                          {reportList &&
                            reportList.map((r) => (
                              <tr key={r.id}>
                                <td>{r.year}</td>
                                <td>
                                  <Translator
                                    file="user"
                                    name={`calendar.month${r.month}`}
                                  />
                                </td>
                                <td>{renderCommaFloat(r.totalWaterWaste)}</td>
                                <td>
                                  {reportOptions.wasteOrigin &&
                                    reportOptions.wasteOrigin.find(
                                      (o) => o.value === r.wasteOrigin,
                                    ).label}
                                </td>
                                <td>
                                  {reportOptions.wasteDestination &&
                                    reportOptions.wasteDestination.find(
                                      (o) => o.value === r.wasteDestination,
                                    ).label}
                                </td>
                                <td>
                                  {r.evidence && (
                                    <span
                                      title={r.evidence.name}
                                      className="mr-1 pointer"
                                      onClick={() =>
                                        getWaterWasteEvidence(r.id, r.evidence.salt)
                                      }
                                    >
                                      <AppRemixIcon icon="file-text" />
                                    </span>
                                  )}
                                </td>
                                <td>{r.comment}</td>
                                <td className="text-center">
                                  <span
                                    className="btn btn-info btn-xs mr-1"
                                    onClick={() => handleEditReport(r)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="edit"
                                      />,
                                    )}
                                  >
                                    <AppRemixIcon icon="edit" />
                                  </span>
                                  <span
                                    className="btn btn-danger btn-xs"
                                    onClick={() => setDeleteReportId(r.id)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="delete"
                                      />,
                                    )}
                                  >
                                    <AppRemixIcon icon="delete-bin-6" />
                                  </span>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="alert alert-info">
                        <Translator file="module-saicaReports" name="no.results" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <AppModal
          show={showSendReportModal}
          title={ReactDOMServer.renderToString(
            <Translator file="module-comsaReports" name="report.form" />,
          )}
          size="lg"
          onClose={() => setShowSendReportModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={reportFormInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSaveReport}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={values.year}
                        onChange={(opt) => setFieldValue('year', opt)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                        popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      />
                      {errors.year && (
                        <div className="text-danger">*{errors.year}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppSingleSelect
                        options={months}
                        onChange={(opt) => setFieldValue('month', opt)}
                        title={<Translator file="module-saicaReports" name="month" />}
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={values.month}
                      />
                      {errors.month && (
                        <div className="text-danger">*{errors.month}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <FieldArray
                      name="dynamicArea"
                      render={(arrayHelpers) => (
                        <>
                          {values.dynamicArea.map((tv, i) => (
                            <div
                              key={i}
                              className="row pt-2 pb-2"
                              style={{ borderBottom: '1px solid #f4f6f9' }}
                            >
                              <div className="col-lg-12">
                                <button
                                  className="btn btn-danger btn-xs float-right"
                                  onClick={() =>
                                    removeDynamicArea(
                                      values.dynamicArea,
                                      arrayHelpers,
                                      i,
                                    )
                                  }
                                  disabled={values.dynamicArea.length === 1}
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="delete"
                                    />,
                                  )}
                                >
                                  <AppRemixIcon icon="delete-bin-6" />
                                </button>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <AppInputNumber
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="total.water.waste"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="contrast-drop" />}
                                    value={values.dynamicArea[i].totalWaterWaste}
                                    onChange={(value) =>
                                      setFieldValue(
                                        `dynamicArea[${i}].totalWaterWaste`,
                                        value,
                                      )
                                    }
                                    placeholder={
                                      <Translator
                                        file="module-comsaReports"
                                        name="enter.a.quantity"
                                      />
                                    }
                                    decimals={3}
                                    append={[
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>,
                                    ]}
                                  />
                                  {errors?.dynamicArea &&
                                    errors.dynamicArea[i]?.totalWaterWaste && (
                                      <div className="text-danger">
                                        *{errors.dynamicArea[i].totalWaterWaste}
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <AppSingleSelect
                                    options={reportOptions.wasteOrigin}
                                    onChange={(opt) =>
                                      setFieldValue(
                                        `dynamicArea[${i}].wasteOrigin`,
                                        opt,
                                      )
                                    }
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="waste.origin"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="contrast-drop-2" />}
                                    placeholder={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="select.a.type"
                                      />,
                                    )}
                                    value={values.dynamicArea[i].wasteOrigin}
                                  />
                                  {errors?.dynamicArea &&
                                    errors.dynamicArea[i]?.wasteOrigin && (
                                      <div className="text-danger">
                                        *{errors.dynamicArea[i].wasteOrigin}
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <AppSingleSelect
                                    options={reportOptions.wasteDestination}
                                    onChange={(opt) =>
                                      setFieldValue(
                                        `dynamicArea[${i}].wasteDestination`,
                                        opt,
                                      )
                                    }
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="waste.destination"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="contrast-drop-2" />}
                                    placeholder={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="select.a.type"
                                      />,
                                    )}
                                    value={values.dynamicArea[i].wasteDestination}
                                  />
                                  {errors?.dynamicArea &&
                                    errors.dynamicArea[i]?.wasteDestination && (
                                      <div className="text-danger">
                                        *{errors.dynamicArea[i].wasteDestination}
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="col-12">
                                <AppTextArea
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="m-cr-comment"
                                    />,
                                  )}
                                  value={values?.comment}
                                  onChange={(v) => setFieldValue(`comment`, v)}
                                  height="89px"
                                />
                              </div>
                              <div className="col-12 md-6 mb-3">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="attach.evidence"
                                  />
                                </label>
                                <div>
                                  <div className="d-flex">
                                    {!!values.evidence &&
                                      !(values.evidence instanceof FileList) && (
                                        <span
                                          title={ReactDOMServer.renderToString(
                                            <Translator
                                              file="module-saicaReports"
                                              name="download.evidence"
                                            />,
                                          )}
                                          className="btn btn-info mr-1 pointer"
                                          onClick={() =>
                                            getWaterWasteEvidence(
                                              values.id,
                                              values.evidence.salt,
                                              dispatch,
                                            )
                                          }
                                        >
                                          <AppRemixIcon icon="file-text" />
                                        </span>
                                      )}
                                    <AppFileUploader
                                      btnText={
                                        <Translator file="general" name="search..." />
                                      }
                                      placeholder={
                                        !values.evidence
                                          ? ReactDOMServer.renderToString(
                                              <Translator
                                                file="general"
                                                name="select.a.file"
                                              />,
                                            )
                                          : ReactDOMServer.renderToString(
                                              <Translator
                                                file="general"
                                                name="one.file.selected"
                                              />,
                                            )
                                      }
                                      onChange={(opt) =>
                                        setFieldValue('evidence', opt)
                                      }
                                      id={i * 2}
                                    />
                                  </div>
                                  {!!values.evidence &&
                                    !(values.evidence instanceof FileList) && (
                                      <div>
                                        <label className="mb-0">
                                          <Translator
                                            file="module-comsaReports"
                                            name="delete"
                                          />
                                        </label>
                                        <label className="mt-1 pointer font-weight-light float-right">
                                          <input
                                            type="checkbox"
                                            className="mr-1"
                                            value={values.deleteEvidence}
                                            onChange={() =>
                                              setFieldValue(
                                                'deleteEvidence',
                                                !values.deleteEvidence,
                                              )
                                            }
                                            checked={values.deleteEvidence}
                                          />
                                          {values.evidence.name}
                                        </label>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {!values.id && (
                            <div className="row mt-3 mb-3">
                              <div className="col-12">
                                <span
                                  className="pointer"
                                  onClick={() =>
                                    arrayHelpers.push({
                                      totalWaterWaste: '',
                                      wasteOrigin: null,
                                      wasteDestination: null,
                                    })
                                  }
                                  ref={dynamicAreaButton}
                                >
                                  <AppRemixIcon icon="add-box" />{' '}
                                  <Translator
                                    file="module-comsaReports"
                                    name="add.more"
                                  />
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <button
                      className="btn btn-success btn-block"
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isSendingReport}
                    >
                      <Translator
                        file="module-comsaReports"
                        name={values.id ? 'edit' : 'add'}
                      />
                    </button>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </AppModal>
        <AppModal
          show={showDeleteModal}
          type="danger"
          isConfirmBtn
          isCancelBtn
          confirmBtnText={<Translator file="module-comsaReports" name="delete" />}
          title={<Translator file="module-comsaReports" name="delete.report" />}
          onClose={handleOnDeleteModalClose}
          onConfirm={handleOnDeleteModalConfirm}
        >
          <Translator file="module-comsaReports" name="confirm.delete.report" />
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleComsaWaterWasteReportScreen;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
